import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Pause, X, Volume2, VolumeX, Timer } from "lucide-react";
import { toast } from "sonner";
import { api, todayStr } from "../lib/api";
import { TutorAvatar } from "../components/TutorAvatar";
import { ShareWinCard } from "../components/ShareWinCard";

const DURATIONS = [10, 15, 25];
const NUDGE_INTERVAL = 5 * 60;

export default function FocusPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("setup");
  const [minutes, setMinutes] = useState(15);
  const [task, setTask] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [paused, setPaused] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [nudge, setNudge] = useState("");
  const audioRef = useRef(null);
  const nudgedAt = useRef(new Set());
  const voiceRef = useRef(true);
  voiceRef.current = voiceOn;

  const speak = useCallback(async (text) => {
    setNudge(text);
    if (!voiceRef.current) return;
    try {
      audioRef.current?.pause();
      const r = await api.post("/tts", { text });
      const audio = new Audio(`data:audio/mp3;base64,${r.data.audio_base64}`);
      audioRef.current = audio;
      setSpeaking(true);
      audio.onended = () => setSpeaking(false);
      audio.onerror = () => setSpeaking(false);
      await audio.play();
    } catch {
      setSpeaking(false);
    }
  }, []);

  const getNudge = useCallback(async (p, elapsedMin, totalMin, taskText) => {
    try {
      const r = await api.post("/focus/nudge", { task: taskText, elapsed_min: elapsedMin, total_min: totalMin, phase: p });
      speak(r.data.text);
    } catch (e) {
      console.error("Nudge failed", e);
      setNudge("Still right here with you — keep going.");
    }
  }, [speak]);

  const start = () => {
    setSecondsLeft(minutes * 60);
    nudgedAt.current = new Set();
    setPhase("running");
    setPaused(false);
    getNudge("start", 0, minutes, task);
  };

  useEffect(() => {
    if (phase !== "running" || paused) return;
    const iv = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(iv);
  }, [phase, paused]);

  useEffect(() => {
    if (phase !== "running") return;
    if (secondsLeft <= 0) {
      setPhase("done");
      getNudge("end", minutes, minutes, task);
      api.post("/focus/complete", { task, minutes, date: todayStr() });
      toast.success(`${minutes}-minute sprint complete! Massive win.`);
      return;
    }
    const elapsed = minutes * 60 - secondsLeft;
    if (elapsed > 0 && elapsed % NUDGE_INTERVAL === 0 && !nudgedAt.current.has(elapsed)) {
      nudgedAt.current.add(elapsed);
      getNudge("mid", Math.round(elapsed / 60), minutes, task);
    }
  }, [secondsLeft, phase, minutes, task, getNudge]);

  const quit = () => {
    audioRef.current?.pause();
    setSpeaking(false);
    navigate("/");
  };

  const mm = String(Math.max(0, Math.floor(secondsLeft / 60))).padStart(2, "0");
  const ss = String(Math.max(0, secondsLeft % 60)).padStart(2, "0");
  const progress = phase === "running" || phase === "done" ? 1 - secondsLeft / (minutes * 60) : 0;
  const R = 120;
  const C = 2 * Math.PI * R;

  if (phase === "setup") {
    return (
      <div className="max-w-lg mx-auto px-5 pt-8 pb-32 space-y-6">
        <div className="flex items-center gap-3">
          <Timer size={30} />
          <h1 className="font-heading text-3xl font-semibold">Focus sprint</h1>
        </div>
        <p className="text-gray-600 text-sm">Mia sits with you while you work — she'll check in every 5 minutes so your brain stays anchored. Pick a sprint, name the task, go.</p>
        <div className="flex gap-3">
          {DURATIONS.map((d) => (
            <button
              key={d}
              onClick={() => setMinutes(d)}
              data-testid={`duration-${d}-button`}
              className={`flex-1 py-4 border-2 border-black rounded-2xl font-heading text-xl font-semibold transition-all ${
                minutes === d ? "bg-[#4D96FF] shadow-[4px_4px_0px_rgba(0,0,0,1)]" : "bg-white shadow-[2px_2px_0px_rgba(0,0,0,1)] text-gray-500"
              }`}
            >
              {d}<span className="text-sm font-normal"> min</span>
            </button>
          ))}
        </div>
        <input
          className="w-full bg-white border-2 border-black rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[#FFE066]/50 text-base"
          placeholder="What are you working on? (optional)"
          value={task}
          onChange={(e) => setTask(e.target.value)}
          data-testid="focus-task-input"
        />
        <button
          onClick={start}
          data-testid="focus-start-button"
          className="w-full bg-[#FFE066] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold px-6 py-4 flex items-center justify-center gap-2 text-lg"
        >
          <Play size={20} /> Start sprint with Mia
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-5 pt-8 pb-32 flex flex-col items-center gap-6 text-center">
      <div className="relative">
        <svg width="280" height="280" className="-rotate-90">
          <circle cx="140" cy="140" r={R} fill="none" stroke="#0A0A0A" strokeWidth="4" opacity="0.1" />
          <circle
            cx="140" cy="140" r={R} fill="none"
            stroke={phase === "done" ? "#51CF66" : "#4D96FF"} strokeWidth="10" strokeLinecap="round"
            strokeDasharray={C} strokeDashoffset={C * (1 - progress)}
            style={{ transition: "stroke-dashoffset 1s linear" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
          <TutorAvatar speaking={speaking} size="w-20 h-20" />
          <p className="font-heading text-4xl font-semibold" data-testid="focus-timer">
            {phase === "done" ? "Done!" : `${mm}:${ss}`}
          </p>
        </div>
      </div>

      {task && <p className="text-sm font-bold text-gray-700" data-testid="focus-task-label">Working on: {task}</p>}

      {nudge && (
        <div className="bg-white border-2 border-black rounded-2xl shadow-[4px_4px_0px_rgba(0,0,0,1)] p-4 text-sm text-left w-full" data-testid="focus-nudge-bubble">
          <span className="font-bold">Mia:</span> {nudge}
        </div>
      )}

      {phase === "done" && (
        <ShareWinCard
          stat={`${minutes} min`}
          title="Focus sprint complete!"
          sub={task ? `Locked in on: ${task}` : "Body-doubled with Mia"}
          color="#51CF66"
          testId="share-sprint-button"
          className="w-full"
        />
      )}

      <div className="flex gap-3 w-full">
        {phase === "running" && (
          <button
            onClick={() => setPaused(!paused)}
            data-testid="focus-pause-button"
            className="flex-1 bg-[#FFE066] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold py-3 flex items-center justify-center gap-2"
          >
            {paused ? <><Play size={18} /> Resume</> : <><Pause size={18} /> Pause</>}
          </button>
        )}
        <button
          onClick={() => setVoiceOn(!voiceOn)}
          data-testid="focus-voice-toggle"
          className={`p-3 rounded-full border-2 border-black shadow-[3px_3px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all ${voiceOn ? "bg-[#F2C6DE]" : "bg-white"}`}
        >
          {voiceOn ? <Volume2 size={18} /> : <VolumeX size={18} />}
        </button>
        <button
          onClick={quit}
          data-testid="focus-quit-button"
          className={`border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold py-3 px-5 flex items-center justify-center gap-2 ${
            phase === "done" ? "flex-1 bg-[#51CF66]" : "bg-white"
          }`}
        >
          <X size={18} /> {phase === "done" ? "Back home" : "Quit"}
        </button>
      </div>
    </div>
  );
}
